export const LIVE_DRAWING_API_VERSION = "2";
export const AI_DRAWING_IDLE_MS = 2 * 60_000;
export const AI_DRAWING_EXPIRY_MS = 10 * 60_000;
export const LIVE_DRAWING_PAGE_BYTES = 24 * 1024;
/** Keep page text below common clients' model-visible truncation threshold. */
export const LIVE_DRAWING_PAGE_TARGET_BYTES = 16 * 1024;
export const LIVE_DRAWING_RESULT_BYTES = 64 * 1024;
/** Live relay/cache budget, independent of the larger cloud file allowance. */
export const MAX_LIVE_SCENE_SIZE_BYTES = 4 * 1024 * 1024;
export const LIVE_DRAWING_OUTLINE_DEFAULT_GAP = 100;
export const LIVE_DRAWING_OUTLINE_MAX_GAP = 10_000;
export const LIVE_DRAWING_BBOX_MODES = ["intersects", "contains"];
/** Server-owned recovery text per exact cause; the wire carries only the reason key. */
export const LIVE_DRAWING_REASON_GUIDANCE = {
    STALE_CONTEXT_EPOCH: {
        nextAction: "read_then_retry",
        hint: "The revision comes from an earlier context state. Call drawing_read and use the revision it returns.",
    },
    REVISION_FROM_FUTURE: {
        nextAction: "read_then_retry",
        hint: "The revision is newer than anything observed. Use only a revision returned by drawing_read or a mutation receipt.",
    },
    REVISION_NOT_CURRENT: {
        nextAction: "read_then_retry",
        hint: "This operation needs the exact current revision. Call drawing_read and reconsider against the fresh scene. For drawing_replace, rebuild the complete replacement scene from that snapshot; then resubmit with its revision and a new operationId.",
    },
    STALE_PRECONDITION: {
        nextAction: "read_then_retry",
        hint: "The listed elements changed since you read them. Call drawing_read, rebuild the patch and preconditions from the fresh fingerprints, and use a new operationId.",
    },
    MISSING_PRECONDITION: {
        nextAction: "fix_arguments",
        hint: "Add a precondition with the drawing_read or drawing_find fingerprint for each listed element ID, including elements bound to, grouped with or framed with the ones you touch. New add IDs need no precondition.",
    },
    CHANGED_SINCE_REVISION: {
        nextAction: "read_then_retry",
        hint: "The listed elements matched by a selector operation, or their dependents, changed after ifUnchangedSince. Nothing was applied. Run drawing_find again, review the changes, and resend with its revision and a new operationId.",
    },
    SELECTOR_NO_MATCH: {
        nextAction: "fix_arguments",
        hint: "The deleteWhere or updateWhere selector matched no element. Check it with drawing_find, or remove the operation.",
    },
    FIND_CURSOR_STALE: {
        nextAction: "read_then_retry",
        hint: "The drawing or the query changed since this drawing_find cursor was issued. Run drawing_find again without a cursor.",
    },
    DUPLICATE_ELEMENT_ID: {
        nextAction: "fix_arguments",
        hint: "An add reuses an element ID that already exists. Choose fresh IDs or use an update operation, and use a new operationId.",
    },
    UNKNOWN_ELEMENT_ID: {
        nextAction: "read_then_retry",
        hint: "An update or delete targets an element ID that is not in the scene. Call drawing_read and use IDs from it.",
    },
    INVALID_PRECONDITION: {
        nextAction: "fix_arguments",
        hint: "Each precondition needs a unique non-empty elementId and an expectedFingerprint that is null or 64 lowercase hex characters.",
    },
    DUPLICATE_OPERATION_TARGET: {
        nextAction: "fix_arguments",
        hint: "One batch touches the same element more than once. Merge those operations into one per element.",
    },
    EMPTY_OPERATIONS: {
        nextAction: "fix_arguments",
        hint: "Send at least one operation.",
    },
    TOO_MANY_OPERATIONS: {
        nextAction: "fix_arguments",
        hint: "A batch holds at most 200 affected elements. Batch new elements together in add.elements. Split only at the element or 2 MiB request limit; read deltas when new fingerprints are needed.",
    },
    IMMUTABLE_FIELD: {
        nextAction: "fix_arguments",
        hint: "An update patch must not set id or type. Delete the element and add a new one to change its type.",
    },
    ELEMENT_TYPE_CHANGE: {
        nextAction: "fix_arguments",
        hint: "An element cannot change type. Delete it and add a new element instead.",
    },
    ELEMENT_SCHEMA_INVALID: {
        nextAction: "fix_arguments",
        hint: "The listed elements do not satisfy the advertised element schema; see issues for the failing paths. Add elements may omit defaultable fields, but every supplied field must be valid. Update patches are partial, must not set id or type, and receive no defaults; replacement scenes need complete elements.",
    },
    INTEGRITY_VIOLATION: {
        nextAction: "fix_arguments",
        hint: "The result would break a binding, container, frame, ordering or size rule; see issues for the rule and element. Fix the references or include their reciprocal updates in the same batch.",
    },
    PAYLOAD_TOO_LARGE: {
        nextAction: "fix_arguments",
        hint: "The request exceeds the 2 MiB limit or contains non-JSON values. Split it into smaller patches.",
    },
    SCENE_TOO_LARGE: {
        nextAction: "stop",
        hint: "The drawing would exceed the 4 MiB scene limit. Remove content or ask the user to split the drawing.",
    },
    SCENE_METADATA_CHANGED: {
        nextAction: "fix_arguments",
        hint: "drawing_replace changes elements only. Send appState and files exactly as read.",
    },
    OPERATION_ID_INVALID: {
        nextAction: "fix_arguments",
        hint: "Send a non-empty operationId of at most 100 characters, unique for each distinct operation.",
    },
    OPERATION_ID_REUSED: {
        nextAction: "fix_arguments",
        hint: "That operationId was used for different content. Reuse an operationId only to retry the identical request; otherwise send a new one.",
    },
    SCHEMA_VIOLATION: {
        nextAction: "fix_arguments",
        hint: "The arguments do not match the tool input schema; see issues for the JSON path and schema keyword. Correct them against the advertised schema.",
    },
    IMAGE_NOT_ALLOWED: {
        nextAction: "fix_arguments",
        hint: "Assistants cannot create images or change an image's file. Move, resize, group or delete existing images only.",
    },
    HIDDEN_ELEMENT: {
        nextAction: "fix_arguments",
        hint: "The target is an unsupported element that is preserved but not editable by assistants. Leave it untouched.",
    },
    READ_ONLY_ACCESS: {
        nextAction: "ask_user",
        hint: "This share code grants read-only access. Ask the user to share the drawing with edit access.",
    },
    OWNER_ONLY: {
        nextAction: "ask_user",
        hint: "Only the drawing owner can delete it. Ask the owner to delete it.",
    },
    TOO_MANY_CONTEXTS: {
        nextAction: "wait_then_retry",
        hint: "Too many open drawing contexts. Call drawing_close on contexts you no longer need, or wait for them to expire, then retry.",
    },
    RECEIPT_CAPACITY_REACHED: {
        nextAction: "reopen",
        hint: "This context retained its maximum 256 operation receipts. Call drawing_close, reopen the drawing, call drawing_read, and reconsider before editing.",
    },
};
/** Fallback recovery guidance for errors that carry no specific reason. */
export const LIVE_DRAWING_CODE_GUIDANCE = {
    DRAWING_CONTEXT_EXPIRED: {
        nextAction: "reopen",
        hint: "Call drawing_open with the AI share code (or drawing_create), then drawing_read, and reconsider before editing. Do not replay old edits.",
    },
    AUTH_REQUIRED: {
        nextAction: "ask_user",
        hint: "Delegated authorization expired or is missing. Ask the user to sign in again, then reopen the drawing.",
    },
    ACCESS_DENIED: {
        nextAction: "ask_user",
        hint: "This caller may not perform the operation on this drawing. Ask the user to check sharing and the AI share code.",
    },
    GRANT_NOT_FOUND: {
        nextAction: "ask_user",
        hint: "No active AI share code matches. Ask the user to share the drawing with AI again and give you the new code.",
    },
    GRANT_REVOKED: {
        nextAction: "ask_user",
        hint: "The AI share code was revoked. Ask the user to share the drawing again.",
    },
    RESYNC_REQUIRED: {
        nextAction: "read_then_retry",
        hint: "Call drawing_read and reconsider your edits against the fresh scene.",
    },
    REVISION_CONFLICT: {
        nextAction: "read_then_retry",
        hint: "The request conflicts with the live drawing. Call drawing_read, rebuild the request from the fresh snapshot, and use a new operationId.",
    },
    IDEMPOTENCY_CONFLICT: {
        nextAction: "fix_arguments",
        hint: "Use a new operationId unless this is an identical retry.",
    },
    INVALID_ARGUMENT: {
        nextAction: "fix_arguments",
        hint: "Correct the arguments to match the tool input schema.",
    },
    INVALID_OPERATION: {
        nextAction: "fix_arguments",
        hint: "Correct the operation; the same request will fail again.",
    },
    UNSUPPORTED_ASSET: {
        nextAction: "fix_arguments",
        hint: "Assistants cannot create images, change image files, or edit unsupported elements.",
    },
    PERSISTENCE_CONFLICT: {
        nextAction: "read_then_retry",
        hint: "Drawing storage changed underneath the live session. Call drawing_read and reconsider.",
    },
    PERSISTENCE_UNAVAILABLE: {
        nextAction: "wait_then_retry",
        hint: "Storage is temporarily unavailable. Your live edits are retained; wait, then call drawing_read to confirm persistence.saved.",
    },
    ROOM_UNAVAILABLE: {
        nextAction: "wait_then_retry",
        hint: "The live drawing service is temporarily unavailable. Retry after a short delay; if it persists, call drawing_open again.",
    },
    RATE_LIMITED: {
        nextAction: "wait_then_retry",
        hint: "Too many pending operations. Wait a few seconds, then retry.",
    },
    MIGRATION_REQUIRED: {
        nextAction: "fix_arguments",
        hint: "Use drawing_open(code) or drawing_create(name), then drawing_read/patch/replace/close/delete with the returned handle.",
    },
};
export const LIVE_DRAWING_ERROR_LIST_LIMIT = 20;
const MAX_FACT_TEXT = 100;
const SAFE_FACT_TEXT = /^[\w.\-[\]:/@~]+$/;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function safeText(value) {
    return typeof value === "string" &&
        value.length > 0 &&
        value.length <= MAX_FACT_TEXT &&
        SAFE_FACT_TEXT.test(value)
        ? value
        : undefined;
}
export function isLiveDrawingErrorReason(value) {
    return typeof value === "string" && Object.hasOwn(LIVE_DRAWING_REASON_GUIDANCE, value);
}
/** Drop everything outside the allowlist, cap lists, and refuse free-form text. */
export function sanitizeLiveDrawingErrorFacts(value) {
    if (!isRecord(value) || !isLiveDrawingErrorReason(value.reason))
        return undefined;
    const facts = { reason: value.reason };
    for (const key of ["baselineRevision", "currentRevision"]) {
        const revision = value[key];
        if (isRecord(revision) &&
            typeof revision.epoch === "string" &&
            revision.epoch.length > 0 &&
            revision.epoch.length <= 100 &&
            Number.isSafeInteger(revision.sequence) &&
            revision.sequence >= 0) {
            facts[key] = { epoch: revision.epoch, sequence: revision.sequence };
        }
    }
    if (value.applied === false)
        facts.applied = false;
    let truncated = value.truncated === true;
    if (Number.isSafeInteger(value.operationIndex) && value.operationIndex >= 0) {
        facts.operationIndex = value.operationIndex;
    }
    if (Array.isArray(value.elementIds)) {
        const ids = value.elementIds.map(safeText).filter((id) => !!id);
        if (ids.length > LIVE_DRAWING_ERROR_LIST_LIMIT)
            truncated = true;
        if (ids.length)
            facts.elementIds = ids.slice(0, LIVE_DRAWING_ERROR_LIST_LIMIT);
    }
    const field = safeText(value.field);
    if (field)
        facts.field = field;
    if (Array.isArray(value.issues)) {
        const issues = [];
        for (const raw of value.issues) {
            if (!isRecord(raw))
                continue;
            const code = safeText(raw.code);
            if (!code)
                continue;
            const issue = { code };
            const elementId = safeText(raw.elementId);
            const path = safeText(raw.path);
            if (elementId)
                issue.elementId = elementId;
            if (path)
                issue.path = path;
            issues.push(issue);
        }
        if (issues.length > LIVE_DRAWING_ERROR_LIST_LIMIT)
            truncated = true;
        if (issues.length)
            facts.issues = issues.slice(0, LIVE_DRAWING_ERROR_LIST_LIMIT);
    }
    if (truncated)
        facts.truncated = true;
    return facts;
}
/** Complete agent-facing details: reason-specific guidance, else the code's default. */
export function liveDrawingErrorDetails(code, facts) {
    const guidance = facts
        ? LIVE_DRAWING_REASON_GUIDANCE[facts.reason]
        : LIVE_DRAWING_CODE_GUIDANCE[code];
    return { ...facts, nextAction: guidance.nextAction, hint: guidance.hint };
}
/** Stable JSON for scene fingerprints and exact retry-body comparison. */
export function canonicalSceneJson(value) {
    if (value === null || typeof value !== "object") {
        const encoded = JSON.stringify(value);
        if (encoded === undefined)
            throw new TypeError("Scene data must contain JSON values");
        return encoded;
    }
    if (Array.isArray(value))
        return `[${value.map(canonicalSceneJson).join(",")}]`;
    return `{${Object.keys(value)
        .filter((key) => value[key] !== undefined)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${canonicalSceneJson(value[key])}`)
        .join(",")}}`;
}
/** Content identity for save coordination, independent of aggregate element versions. */
export async function fingerprintCollabElements(elements) {
    const bytes = new TextEncoder().encode(canonicalSceneJson(elements));
    const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
//# sourceMappingURL=liveDrawing.js.map